import { NgxPermissionsService } from 'ngx-permissions';
import { map, withLatestFrom } from 'rxjs';

import {
  PERMISSION_CREATE,
  PERMISSION_CREATE_ALL,
  PERMISSION_DELETE,
  PERMISSION_DELETE_ALL,
  PERMISSION_READ,
  PERMISSION_READ_ALL,
  PERMISSION_UPDATE,
  PERMISSION_UPDATE_ALL,
  RouterFacadeInterface,
  SharedEntityFacadeInterface,
} from '@ideapa/app-common';
import { FieldConfig, FilterValue } from '@ideapa/common-interfaces';
import { DeepPartial } from '@ideapa/common-utils';
import { select, Store } from '@ngrx/store';

import { SharedEntityActions, SharedEntityStateSelectors } from './models';

export abstract class BaseSharedEntityFacade<T, V>
  implements SharedEntityFacadeInterface<T>
{
  allEntities$ = this.store.pipe(select(this.selectors.getAll));

  loadedAll$ = this.store.pipe(select(this.selectors.loadedAll));

  canDelete$ = this.permissionsService.permissions$.pipe(
    withLatestFrom(
      this.routerFacade.isViewOnlyMode$,
      this.routerFacade.isFindMode$
    ),
    map(([p, isViewOnlyMode, isFindMode]) => {
      if (isViewOnlyMode || isFindMode) return false;
      return (
        p[PERMISSION_DELETE_ALL] != null ||
        p[PERMISSION_DELETE + '_' + this.entityName] != null
      );
    })
  );

  canUpdate$ = this.permissionsService.permissions$.pipe(
    withLatestFrom(
      this.routerFacade.isViewOnlyMode$,
      this.routerFacade.isFindMode$
    ),
    map(([p, isViewOnlyMode, isFindMode]) => {
      if (isViewOnlyMode || isFindMode) return false;
      return (
        p[PERMISSION_UPDATE_ALL] != null ||
        p[PERMISSION_UPDATE + '_' + this.entityName] != null
      );
    })
  );

  canCreate$ = this.permissionsService.permissions$.pipe(
    withLatestFrom(
      this.routerFacade.isViewOnlyMode$,
      this.routerFacade.isFindMode$
    ),
    map(([p, isViewOnlyMode, isFindMode]) => {
      if (isViewOnlyMode || isFindMode) return false;
      return (
        p[PERMISSION_CREATE_ALL] != null ||
        p[PERMISSION_CREATE + '_' + this.entityName] != null
      );
    })
  );

  canRead$ = this.permissionsService.permissions$.pipe(
    withLatestFrom(this.routerFacade.isFindMode$),
    map(([p, isFindMode]) => {
      if (isFindMode) return false;

      return (
        p[PERMISSION_READ_ALL] != null ||
        p[PERMISSION_READ + '_' + this.entityName] != null
      );
    })
  );

  constructor(
    protected store: Store<V>,
    private permissionsService: NgxPermissionsService,
    private routerFacade: RouterFacadeInterface,
    protected selectors: SharedEntityStateSelectors<T, V>,
    protected actions: SharedEntityActions<T>,
    private entityName: string
  ) {}

  loadPaged(
    take: number,
    skip: number,
    orderBy?: { [field: string]: 'asc' | 'desc' },
    filters?: FilterValue[],
    join?: string[]
  ) {
    this.store.dispatch(
      this.actions.loadPaged({
        take,
        skip,
        orderBy,
        filters,
        join,
      })
    );
  }

  loadAll() {
    this.store.dispatch(this.actions.loadAll());
  }

  loadPagedSuccess(items: T[]) {
    this.store.dispatch(this.actions.loadPagedSuccess({ items }));
  }

  loadAllSuccess(items: T[]) {
    this.store.dispatch(this.actions.loadAllSuccess({ items }));
  }

  updateManySuccess(items: T[]) {
    this.store.dispatch(this.actions.updateManySuccess({ items: items }));
  }

  loadPagedFailure(error: string) {
    this.store.dispatch(this.actions.loadPagedFailure({ error }));
  }

  add(u: DeepPartial<T>, notify = true, goBack = false) {
    this.store.dispatch(this.actions.add({ item: u, notify, goBack }));
  }

  delete(id: number, notify = true, goBack = true, callback?: () => void) {
    this.store.dispatch(
      this.actions.deleteEntity({ id, notify, goBack, callback })
    );
  }

  clearCache() {
    this.store.dispatch(this.actions.clearCache());
  }
  update(
    id: number,
    u: DeepPartial<T>,
    callback?: (item: T) => void,
    notify = true,
    goBack = false
  ) {
    this.store.dispatch(
      this.actions.update({
        update: { id, changes: u as Partial<T> },
        notify,
        goBack,
        callback,
      })
    );
  }

  loadSingle(id: number) {
    this.store.dispatch(this.actions.loadSingle({ id }));
  }

  downloadExcel(
    fileName?: string,
    columns?: FieldConfig[],
    filters?: FilterValue[],
    orderBy?: { [field: string]: 'asc' | 'desc' },
    join?: string[],
    notify = true
  ) {
    this.store.dispatch(
      this.actions.downloadExcel({
        fileName,
        columns,
        filters,
        orderBy,
        join,
        notify,
      })
    );
  }
}
